import React from 'react';

type NavItem = {
  id: string; // internal id
  label?: string; // visible label (optional)
  selector: string; // DOM selector for target section
};

const SVG_CENTER = 23;
const INACTIVE_SIZE = 10;
const ACTIVE_INNER_SIZE = 14.3036;
const ACTIVE_OUTER_SIZE = 23.0416;
const INACTIVE_FILL = '#FFCAFC';
const ACTIVE_FILL = '#FF8EF9';
const ACTIVE_FILL_POP = '#FFB5FC';
const ACTIVE_STROKE = '#FF6DF7';
const ACTIVE_INNER_OVERSHOOT = ACTIVE_INNER_SIZE + 1.6;
const ACTIVE_OUTER_OVERSHOOT = ACTIVE_OUTER_SIZE + 2.4;

const lerp = (from: number, to: number, t: number) => from + (to - from) * t;

const hexToRgb = (hex: string) => {
  const cleaned = hex.replace('#', '');
  return {
    r: parseInt(cleaned.slice(0, 2), 16),
    g: parseInt(cleaned.slice(2, 4), 16),
    b: parseInt(cleaned.slice(4, 6), 16),
  };
};

const mixColor = (fromHex: string, toHex: string, t: number) => {
  const from = hexToRgb(fromHex);
  const to = hexToRgb(toHex);
  const r = Math.round(lerp(from.r, to.r, t));
  const g = Math.round(lerp(from.g, to.g, t));
  const b = Math.round(lerp(from.b, to.b, t));
  return `rgb(${r}, ${g}, ${b})`;
};

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const easeOutBack = (t: number) => {
  const c1 = 2.25;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

function AnimatedNavIcon({ isActive, itemId }: { isActive: boolean; itemId: string }) {
  const [progress, setProgress] = React.useState(isActive ? 1 : 0);
  const progressRef = React.useRef(progress);

  React.useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  React.useEffect(() => {
    let rafId = 0;
    const from = progressRef.current;
    const to = isActive ? 1 : 0;
    const duration = isActive ? 300 : 320;
    const start = performance.now();

    const tick = (now: number) => {
      const raw = Math.min((now - start) / duration, 1);
      const eased = isActive ? easeOutBack(raw) : easeInOutCubic(raw);
      const next = lerp(from, to, eased);
      progressRef.current = next;
      setProgress(next);

      if (raw < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isActive]);

  const clamped = Math.max(0, Math.min(1, progress));
  const activeSizeProgress = isActive ? easeOutBack(clamped) : clamped;
  const settleStart = 0.62;
  const settleProgress = Math.max(0, Math.min(1, (activeSizeProgress - settleStart) / (1 - settleStart)));
  const innerPreSettle = lerp(INACTIVE_SIZE, ACTIVE_INNER_OVERSHOOT, Math.min(1, activeSizeProgress / settleStart));
  const outerPreSettle = lerp(INACTIVE_SIZE, ACTIVE_OUTER_OVERSHOOT, Math.min(1, activeSizeProgress / settleStart));
  const innerSize = isActive
    ? lerp(innerPreSettle, ACTIVE_INNER_SIZE, settleProgress)
    : lerp(INACTIVE_SIZE, ACTIVE_INNER_SIZE, clamped);
  const outerSize = isActive
    ? lerp(outerPreSettle, ACTIVE_OUTER_SIZE, settleProgress)
    : lerp(INACTIVE_SIZE, ACTIVE_OUTER_SIZE, clamped);
  const innerPos = SVG_CENTER - innerSize / 2;
  const outerPos = SVG_CENTER - outerSize / 2;
  const fillColor =
    clamped < 0.55
      ? mixColor(INACTIVE_FILL, ACTIVE_FILL_POP, clamped / 0.55)
      : mixColor(ACTIVE_FILL_POP, ACTIVE_FILL, (clamped - 0.55) / 0.45);
  const strokeColor = mixColor(INACTIVE_FILL, ACTIVE_STROKE, clamped);

  return (
    <svg width="34" height="34" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g
        filter={clamped > 0.02 ? `url(#filter0_di_191_90_${itemId})` : undefined}
        style={{ opacity: 1 }}
      >
        <rect
          x={innerPos}
          y={innerPos}
          width={innerSize}
          height={innerSize}
          transform={`rotate(-45 ${SVG_CENTER} ${SVG_CENTER})`}
          fill={fillColor}
        />
      </g>
      <g filter={`url(#filter1_d_191_90_${itemId})`} style={{ opacity: clamped }}>
        <rect
          x={outerPos}
          y={outerPos}
          width={outerSize}
          height={outerSize}
          transform={`rotate(-45 ${SVG_CENTER} ${SVG_CENTER})`}
          stroke={strokeColor}
          strokeOpacity={clamped}
          fill="none"
          shapeRendering="crispEdges"
        />
      </g>
      <defs>
        <filter id={`filter0_di_191_90_${itemId}`} x="6.92188" y="6.88672" width="32.2266" height="32.2266" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_191_90"/>
          <feOffset/>
          <feGaussianBlur stdDeviation="2.5"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0.980392 0 0 0 0 0.00392157 0 0 0 0 0.929412 0 0 0 0.8 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_191_90"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_191_90" result="shape"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect2_innerShadow_191_90"/>
          <feOffset/>
          <feGaussianBlur stdDeviation="2"/>
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0.980392 0 0 0 0 0.00392157 0 0 0 0 0.929412 0 0 0 1 0"/>
          <feBlend mode="normal" in2="shape" result="effect2_innerShadow_191_90"/>
        </filter>
        <filter id={`filter1_d_191_90_${itemId}`} x="0" y="0" width="46" height="46" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_191_90"/>
          <feOffset/>
          <feGaussianBlur stdDeviation="2.5"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0.980392 0 0 0 0 0.00392157 0 0 0 0 0.929412 0 0 0 1 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_191_90"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_191_90" result="shape"/>
        </filter>
      </defs>
    </svg>
  );
}

export default function RightImageNav({
  items,
  activeId,
  onPlaySound,
  onHoverSound,
  moveToSectionById,
}: {
  items: NavItem[];
  activeId?: string | null;
  onPlaySound?: () => void;
  onHoverSound?: () => void;
  moveToSectionById?: (id: string) => void;
}) {
  const handleClick = (selector: string,id: string, e: React.MouseEvent) => {
    e.preventDefault();
    console.log(selector);
    if (onPlaySound) {
      onPlaySound();
    }
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) {
      moveToSectionById?.(id);
    }
  };

  const handleHover = () => {
    if (onHoverSound) {
      onHoverSound();
    }
  };

  return (
    <nav className="right-image-nav" aria-label="페이지 네비게이션">
      <ul>
        {items.map(item => {
          const isActive = activeId === item.id;

          return (
          <li key={item.id} className={isActive ? 'active' : ''}>
            <button
              type="button"
              className="right-image-nav-btn"
              onClick={(e) => handleClick(item.selector, item.id, e)}
              onMouseEnter={handleHover}
              aria-label={item.label || item.id}
            >
              <span className="right-image-nav-icon-wrap">
                <AnimatedNavIcon isActive={isActive} itemId={item.id} />
              </span>
            </button>
          </li>
          );
        })}
      </ul>
    </nav>
  );
}
